import React, {useContext, useEffect, useMemo, useRef} from "react";
import PhoneSimulatorBody from "../../../../../../components/base/device-simulator/phone-entities/body";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../index";
import {Col} from "reactstrap";
import UserPhoneHomepageComponent from "../../components/component";
import {UserHomepageControllerEvents, UserHomepageViewMode} from "../../../../models";
import UserHomepageUtils from "../../../../core/utils";

const UserPhoneHomepageBody = () => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const newComponent = useRef(undefined);

    const selectedTab = useMemo(() => {
        if (!state.selection.tabId)
            return null;
        return state.updatedData?.tabs?.find(e => e.id === state.selection.tabId)
    }, [state.selection.tabId, state.updatedData?.tabs])

    const loading = useMemo(() => selectedTab?.loading, [selectedTab])
    const noTabs = useMemo(() => state?.updatedData?.tabs && !state?.updatedData?.tabs.length, [state?.updatedData?.tabs])

    /**
     * With each change in the state or controller:
     * - attaches the event listeners for focusing the body of the phone view on selection of tabs.
     * - attaches an event listener for creation of components so that they can be selected and focused.
     */
    useEffect(() => {
        controller.on(UserHomepageControllerEvents.onTabSelected, focusTab);
        controller.on(UserHomepageControllerEvents.onComponentCreated, awaitStateUpdateOnComponentCreation);
        return () => {
            controller.off(UserHomepageControllerEvents.onTabSelected, focusTab);
            controller.off(UserHomepageControllerEvents.onComponentCreated, awaitStateUpdateOnComponentCreation);
        }
    }, [controller, state.updatedData])

    /**
     * With each change in the updatedData of the state:
     * - checks if a new component has been created, and if so, awaits its appearance in the ui and then scrolls to it.
     */
    useEffect(() => {
        if (!newComponent.current)
            return;
        const component = state?.updatedData?.tabs?.find(e => e.id === newComponent.current.tabId)?.components?.find(e => e.id === newComponent.current.componentId);
        if (!component)
            return;

        newComponent.current = undefined;
        setTimeout(() => {
            document
                .getElementById(component.phoneViewElementId)
                ?.scrollIntoView(true);
        }, 800)
    }, [state.updatedData])

    /**
     * Awaits the update of the state before focusing the newly created component
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     * @param {number} componentId
     */
    const awaitStateUpdateOnComponentCreation = (emitter, tabId, componentId) => {
        newComponent.current = {tabId, componentId};
    }

    /**
     * Scrolls the view to the top so that the components can be viewed from the top.
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     */
    const focusTab = (emitter, tabId) => {
        const tab = UserHomepageUtils.getSelection(state?.updatedData, tabId);
        if (selectedTab?.id === tabId)
            return;
        if (!tab)
            return;
        document.getElementById(state.phoneViewElementId)?.scrollTo({top: 0, left: 0});
    }

    /**
     * Renders the components of the selected tab.
     * @return {JSX.Element[]}
     */
    const components = useMemo(() => {
        const _components = state.viewMode === UserHomepageViewMode.edit
            ? selectedTab?.components
            : UserHomepageUtils.getPreviewComponents(selectedTab?.components)
        return _components?.map(component => (
            <UserPhoneHomepageComponent
                bodyId={state.phoneViewElementId}
                key={component.id}
                component={component}
                tabId={selectedTab.id}
                viewMode={state.viewMode}
            />
        ))
    }, [selectedTab, state.phoneViewElementId, state.viewMode])

    return (
        <>
            <PhoneSimulatorBody
                id={state.phoneViewElementId}
                className={'body'}
            >
                {
                    noTabs &&
                    <>
                        <div className={'d-flex h-100 d-flex align-items-center justify-content-center text-center'}>
                            <p>there is currently no tabs available for your homepage</p>
                        </div>
                    </>
                }
                {
                    !noTabs && loading &&
                    <>
                        <div className={'loading phone-home-page slideshow'}>
                            <div/>
                        </div>
                        <div className={'d-flex flex-wrap'}>
                            {
                                Array(4).fill(null).map((_, e) => (
                                    <Col key={e} xs={6}>
                                        <div className={'loading phone-home-page category'}>
                                            <div/>
                                        </div>
                                    </Col>
                                ))
                            }
                        </div>
                        {
                            Array(2).fill(null).map((_, e) => (
                                <Col key={e} xs={12}>
                                    <div className={'loading phone-home-page part-large'}>
                                        <div/>
                                    </div>
                                </Col>
                            ))
                        }
                        <div className={'d-flex flex-wrap justify-content-center'}>
                            {
                                Array(4).fill(null).map((_, e) => (
                                    <Col key={e} xs={10}>
                                        <div className={'loading phone-home-page part-medium'}>
                                            <div/>
                                        </div>
                                    </Col>
                                ))
                            }
                        </div>
                    </>
                }
                {
                    !noTabs && !loading &&
                    components
                }
            </PhoneSimulatorBody>
        </>
    )
}

export default UserPhoneHomepageBody;
